import React, { useState, useEffect } from "react";
import Joyride, {
  ACTIONS,
  CallBackProps,
  EVENTS,
  STATUS,
  Step,
} from "react-joyride";
import { useTour } from "@/contexts/TourContext";

// Define the steps for the nanny tour
const defaultSteps: Step[] = [
  {
    target: "body",
    content:
      "Welcome to Hirelo! Let's take a quick tour of our platform to help you get started as a nanny.",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: "body",
    content:
      "This is your main dashboard where you can see your recent payslips and important information about your employment.",
    placement: "bottom",
  },
  {
    target: ".employment-information",
    content:
      "Here you can view your employment information, including personal details, tax information, and employment terms.",
    placement: "right",
  },
  {
    target: ".payslip-information",
    content:
      "Access all your payslips in one place. You can view and download your historical payment records anytime.",
    placement: "right",
  },
  {
    target: ".submissions-information",
    content:
      "Submit your working hours, expenses, and other information for each pay period. This is where you'll regularly provide information for your employer to process payroll.",
    placement: "right",
  },
  {
    target: ".pto-information",
    content:
      "Request time off, view your leave balances, and check the status of pending requests. You can see all your leave policies here too.",
    placement: "right",
  },
  {
    target: ".filings-information",
    content:
      "Access important tax documents like P60s, P45s, and other HMRC filings related to your employment.",
    placement: "right",
  },
  {
    target: ".referral-information",
    content:
      "Refer other nannies or parents to Hirelo. You may receive benefits for successful referrals.",
    placement: "right",
    locale: { last: "Finish" },
  },
];

export const NannyGuidedTour: React.FC = () => {
  const { tourStatus, endTour } = useTour();
  const [steps] = useState<Step[]>(defaultSteps);
  const [run, setRun] = useState(false);

  // Start the tour when status is in_progress
  useEffect(() => {
    if (tourStatus === "in_progress") {
      setRun(true);
    } else {
      setRun(false);
    }
  }, [tourStatus]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, index, status, type } = data;

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      // Update state to advance the tour
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);

      // If we're on the last step and going forward, end the tour
      if (nextStepIndex >= steps.length && action === ACTIONS.NEXT) {
        endTour();
      }
    }

    // If the tour is finished or skipped, end it
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      endTour();
    }
  };

  return (
    <>
      <Joyride
        callback={handleJoyrideCallback}
        continuous
        hideCloseButton
        run={run}
        scrollToFirstStep
        showProgress
        showSkipButton
        steps={steps}
        styles={{
          options: {
            arrowColor: "#fff",
            backgroundColor: "#fff",
            primaryColor: "#6366f1", // indigo-500
            textColor: "#334155", // slate-700
            overlayColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 10000,
          },
          buttonNext: {
            backgroundColor: "#6366f1",
          },
          buttonBack: {
            color: "#6366f1",
          },
        }}
      />
    </>
  );
};
